import { matchPath } from "react-router";

import { ROUTES } from "./routes";

/**
 * 「戻る」の宛先を URL に持たせる。
 *
 * 画面は複数の入口から開かれる（案件詳細は 6 箇所）ので、戻り先を固定にすると
 * 遷移元と食い違う。状態の正本を URL にするのはこのリポジトリの既存方針
 * （`features/my-applications/filters-url.ts`）に合わせたもので、リロードや
 * リンク共有でも戻り先が失われない。
 *
 * `from` は起票日フィルタで、`returnTo` は IdP 用の絶対 URL で既に使われているため、
 * キーは `back` を使う。
 */
export const BACK_PARAM = "back";

/** ラベルの出どころ。i18n の解決は呼び出し側（ここでは文字列を作らない）。 */
export type BackLabelSource =
  | { readonly kind: "nav"; readonly key: string }
  | { readonly kind: "common"; readonly key: string };

export type BackTarget = {
  /** 検証済みの内部パス（pathname + search）。 */
  readonly to: string;
  readonly label: BackLabelSource;
};

/**
 * 戻り先として許可する宛先。
 *
 * 「内部パスなら何でも可」ではなくルート形に照合するのは、検証とラベル決定を
 * 1 回の照合で済ませられるため（未知の宛先＝ラベル不明＝拒否、が自動で一致する）。
 * 宛先を増やすときはここに 1 行足す。
 */
const KNOWN_DESTINATIONS: readonly {
  readonly pattern: string;
  readonly label: BackLabelSource;
}[] = [
  { pattern: ROUTES.home, label: { kind: "nav", key: "home" } },
  {
    pattern: ROUTES.myApplications,
    label: { kind: "nav", key: "myApplications" },
  },
  {
    pattern: ROUTES.approvalRequests,
    label: { kind: "nav", key: "approvalRequests" },
  },
  {
    pattern: ROUTES.notifications,
    label: { kind: "nav", key: "notifications" },
  },
  { pattern: ROUTES.allCases, label: { kind: "nav", key: "allCases" } },
  // ナビには無い画面。承認作業に戻す導線なので名前を別に持つ
  {
    pattern: ROUTES.approvalReview,
    label: { kind: "common", key: "approvalReview" },
  },
];

/** URL の膨張と極端な入力を打ち切る。 */
const MAX_LENGTH = 512;

const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

/**
 * 戻り先の文字列を検証して宛先に解決する。
 *
 * URL は利用者が編集できるので信用しない。外部へ飛ばす値（絶対 URL・
 * protocol-relative・スキーム付き）は必ず捨てる。
 */
function resolveDestination(raw: string | null): BackTarget | null {
  if (raw === null) return null;

  // 制御文字は trim の前に見る（trim で消えると `/path\t` 型の細工を見逃す）
  if (CONTROL_CHARS.test(raw)) return null;

  const value = raw.trim();
  if (value === "" || value.length > MAX_LENGTH) return null;
  // `/\evil.example.com` を `//evil.example.com` 相当に扱う実装があるため弾く
  if (value.includes("\\")) return null;
  if (!value.startsWith("/")) return null;
  if (value.startsWith("//")) return null;

  const withoutHash = value.split("#")[0] ?? "";
  const queryAt = withoutHash.indexOf("?");
  const pathname = queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt);
  const search = queryAt === -1 ? "" : withoutHash.slice(queryAt);

  // `.` `..` や `//` の内在を弾く（正規化前提の宛先だけを許可する）
  const segments = pathname.split("/").slice(1);
  if (
    pathname !== "/" &&
    segments.some(
      (segment) => segment === "" || segment === "." || segment === "..",
    )
  ) {
    return null;
  }

  const known = KNOWN_DESTINATIONS.find(
    (entry) => matchPath(entry.pattern, pathname) !== null,
  );
  if (known === undefined) return null;

  return { to: `${pathname}${search}`, label: known.label };
}

/**
 * URL の `back` を読む。信用できない値・未知の宛先は捨てて null
 * （呼び出し側は固定の戻り先に落とす）。
 */
export function parseBackTarget(params: URLSearchParams): BackTarget | null {
  // URLSearchParams.get は decode 済み。自分で decodeURIComponent を呼ばない
  // （二重デコードは事故になる）
  return resolveDestination(params.get(BACK_PARAM));
}

/**
 * 遷移先のパスに戻り先を足す。
 *
 * パスヘルパー（`caseDetailPath` 等）のシグネチャを変えずに合成できるようにしている
 * ——「識別子からパスを作る」責務に文脈を混ぜないため。
 */
export function withBackTarget(path: string, backTo: string | null): string {
  const target = resolveDestination(backTo);
  if (target === null) return path;

  // 自分自身へ戻るリンクは作らない（押しても往復するだけ）
  const pathname = path.split("?")[0]?.split("#")[0];
  if (target.to.split("?")[0] === pathname) return path;

  // hash（案件詳細の `#case-action` など）の**前**に付ける。後ろに付けると
  // クエリが hash の一部になり、location.search が空＝戻り先が丸ごと失われる
  const hashAt = path.indexOf("#");
  const base = hashAt === -1 ? path : path.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : path.slice(hashAt);

  // URLSearchParams は空白を `+` にするので使わない（auth-urls.ts と同じ理由）
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}${BACK_PARAM}=${encodeURIComponent(target.to)}${hash}`;
}

/**
 * 今いる画面を戻り先の文字列にする。hash は含めない（protected-route と同じ）。
 *
 * 既にある `back` は取り除く。残すと戻り先が入れ子になり、URL が指数的に伸びる。
 */
export function backTargetFromLocation(location: {
  readonly pathname: string;
  readonly search: string;
}): string {
  const params = new URLSearchParams(location.search);
  params.delete(BACK_PARAM);

  const search = params.toString();
  return search === "" ? location.pathname : `${location.pathname}?${search}`;
}
